import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { api, track, imgUrl } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import { PageHero } from "./AboutPage";
import { Seo } from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const { settings, L } = useSite();
  useEffect(() => { api.get("/testimonials").then((r) => setReviews(r.data)).catch(() => {}); }, []);

  return (
    <main data-testid="reviews-page">
      <Seo title={PAGE_SEO.reviews.title} description={PAGE_SEO.reviews.description} path="/reviews" />
      <PageHero overline={L.reviewsPage.overline} title={L.reviewsPage.title}
        subtitle={L.reviewsPage.subtitle} />
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {reviews.length === 0 ? (
            <Reveal>
              <div className="border border-slate-200 bg-white p-12 md:p-16 text-center max-w-2xl mx-auto">
                <div className="flex justify-center gap-1 mb-5">{[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-[#005AE0]" />)}</div>
                <h2 className="font-heading font-bold text-[#0B1B3D] text-2xl mb-3">{L.reviewsPage.emptyTitle}</h2>
                <p className="text-slate-500 leading-relaxed">{L.reviewsPage.emptyText}</p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((r, i) => (
                <Reveal key={r.id} delay={(i % 3) * 0.06}>
                  <div className="bg-white border border-slate-200 p-8 h-full flex flex-col">
                    <div className="flex gap-1 mb-4">{[...Array(r.rating || 5)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#005AE0] fill-[#005AE0]" />)}</div>
                    <p className="text-slate-600 leading-relaxed mb-6 flex-1">“{r.text}”</p>
                    <p className="font-heading font-bold text-[#0B1B3D]">{r.name}</p>
                    <p className="text-xs text-slate-400 uppercase tracking-widest">{r.source}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
          <Reveal className="flex flex-wrap items-center justify-center gap-4 mt-16">
            {settings.nextdoor_badge_url && (
              <img src={imgUrl(settings.nextdoor_badge_url)} alt="Nextdoor recognition badge" className="h-20 w-auto object-contain" data-testid="nextdoor-badge" />
            )}
            {settings.nextdoor_url && (
              <a href={settings.nextdoor_url} target="_blank" rel="noreferrer" data-testid="reviews-nextdoor-btn"
                onClick={() => track("nextdoor_click")} className="btn-outline-dark !text-xs">
                {L.reviewsPage.nextdoor} <ExternalLink className="w-4 h-4" />
              </a>
            )}
            {settings.google_business_url && (
              <a href={settings.google_business_url} target="_blank" rel="noreferrer" data-testid="reviews-google-btn"
                onClick={() => track("google_business_click")} className="btn-outline-dark !text-xs">
                {L.reviewsPage.google} <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
